import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/cliente';
import type { Opcion, Rol, UsuarioListado } from '../api/tipos';
import { useSesion } from '../sesion';
import {
  Cargando, Dialogo, DialogoMotivo, Error, Insignia,
} from '../componentes/comunes';

/**
 * Usuarios y accesos.
 *
 * Los 16 usuarios que se migraron del Excel entraron con contraseña
 * temporal: la que traía la hoja se descartó y en su lugar quedó un hash
 * de una contraseña de un solo uso. Por eso muchos aparecen marcados
 * como temporales hasta que entran por primera vez.
 */

/**
 * Si esa cuenta es de administrador.
 *
 * La clave y no el nombre: el nombre se puede editar desde Catálogos y
 * una regla de seguridad que depende de un texto editable deja de ser una
 * regla. La comprobación de verdad la hace la API; ésta sólo decide qué
 * botones tiene sentido enseñar.
 */
function esAdministrador(u: UsuarioListado): boolean {
  return u.roles.some((r) => r.clave === 'admin');
}
export default function Usuarios() {
  const { puede, usuario: yo } = useSesion();

  const [busqueda, setBusqueda] = useState('');
  const [rol, setRol] = useState('');
  const [estado, setEstado] = useState('');
  const [lista, setLista] = useState<{ data: UsuarioListado[]; total: number } | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const [roles, setRoles] = useState<Rol[]>([]);
  const [regiones, setRegiones] = useState<Opcion[]>([]);

  const [creando, setCreando] = useState(false);
  const [editando, setEditando] = useState<UsuarioListado | null>(null);
  const [restableciendo, setRestableciendo] = useState<UsuarioListado | null>(null);
  const [cambiandoEstado, setCambiandoEstado] = useState<UsuarioListado | null>(null);
  const [credencial, setCredencial] = useState<{ correo: string; password: string } | null>(null);

  useEffect(() => {
    api.get<Rol[]>('/roles').then(setRoles).catch(() => undefined);
    api.get<Opcion[]>('/catalogos/regiones/opciones').then(setRegiones).catch(() => undefined);
  }, []);

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    api.get<{ data: UsuarioListado[]; total: number }>('/usuarios', {
      busqueda: busqueda || undefined,
      rol: rol || undefined,
      estado: estado || undefined,
      limite: 100,
    })
      .then(setLista)
      .catch(setError)
      .finally(() => setCargando(false));
  }, [busqueda, rol, estado]);

  useEffect(() => {
    const t = setTimeout(cargar, busqueda ? 300 : 0);
    return () => clearTimeout(t);
  }, [cargar, busqueda]);

  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Usuarios</h1>
          <p className="sub">
            Quién entra al sistema y qué puede hacer. El rol define los
            permisos; la zona, qué datos ve.
          </p>
        </div>
        {puede('usuarios', 'crear') && (
          <button className="primario" onClick={() => setCreando(true)}>Nuevo usuario</button>
        )}
      </div>

      <div className="panel">
        <div className="controles">
          <input
            type="search"
            placeholder="Nombre o correo…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
          <select value={rol} onChange={(e) => setRol(e.target.value)}>
            <option value="">Todos los roles</option>
            {roles.map((r) => <option key={r.clave} value={r.clave}>{r.nombre}</option>)}
          </select>
          <select value={estado} onChange={(e) => setEstado(e.target.value)}>
            <option value="">Todos</option>
            <option value="activo">Sólo activos</option>
            <option value="inactivo">Sólo inactivos</option>
            <option value="temporal">Con contraseña temporal</option>
          </select>
        </div>

        <Error error={error} />

        {cargando && !lista ? <Cargando /> : lista && (
          lista.data.length === 0 ? (
            <div className="vacio">No hay usuarios que cumplan esos filtros.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Correo</th>
                  <th>Roles</th>
                  <th>Zonas</th>
                  <th>Responde por</th>
                  <th>Último acceso</th>
                  <th>Estado</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {lista.data.map((u) => (
                  <tr key={u.id}>
                    <td>{[u.nombre, u.apellidos].filter(Boolean).join(' ')}</td>
                    <td>{u.correo}</td>
                    <td>{u.roles.map((r) => r.nombre).join(', ') || '—'}</td>
                    <td>{u.regiones.length ? u.regiones.map((r) => r.nombre).join(', ') : 'Todas'}</td>
                    {/* De cuáles responde, aparte de cuáles ve. Son dos
                        columnas porque son dos cosas: la 0025 le dio todas
                        las zonas a todos los supervisores, así que la de
                        al lado dice «las catorce» en casi cada renglón y
                        no distingue a nadie. Ésta sí. */}
                    <td>
                      {u.zonasQueCubre.length
                        ? u.zonasQueCubre.map((r) => r.nombre).join(', ')
                        : '—'}
                    </td>
                    <td>{u.ultimoAcceso ? fecha(u.ultimoAcceso) : 'Nunca'}</td>
                    <td>
                      <Insignia activo={u.activo} />
                      {u.passwordTemporal && (
                        <span className="etiqueta no" style={{ marginLeft: 6 }}>Temporal</span>
                      )}
                      {u.mfaHabilitado && (
                        <span className="etiqueta si" style={{ marginLeft: 6 }}>2FA</span>
                      )}
                    </td>
                    <td className="acciones">
                      {/*
                        La cuenta de un administrador sólo la toca él
                        mismo, ni siquiera otro administrador. La API lo
                        rechaza igual; esconder los botones evita ofrecer
                        tres cosas que van a fallar y explicarlo después.
                      */}
                      {puede('usuarios', 'editar') && (
                        esAdministrador(u) && u.id !== yo?.id ? (
                          <span className="ayuda">Sólo él mismo</span>
                        ) : (
                          <>
                            <button className="enlace" onClick={() => setEditando(u)}>Editar</button>
                            <button className="enlace" onClick={() => setRestableciendo(u)}>
                              Restablecer
                            </button>
                            {u.id !== yo?.id && (
                              <button className="enlace" onClick={() => setCambiandoEstado(u)}>
                                {u.activo ? 'Desactivar' : 'Reactivar'}
                              </button>
                            )}
                          </>
                        )
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>

      {(creando || editando) && (
        <FormularioUsuario
          roles={roles}
          regiones={regiones}
          usuario={editando}
          onCerrar={() => { setCreando(false); setEditando(null); }}
          onGuardado={(nueva) => {
            setCreando(false);
            setEditando(null);
            if (nueva) setCredencial(nueva);
            cargar();
          }}
        />
      )}

      {restableciendo && (
        <Restablecer
          usuario={restableciendo}
          onCerrar={() => setRestableciendo(null)}
          onHecho={(password) => {
            setCredencial({ correo: restableciendo.correo, password });
            setRestableciendo(null);
            cargar();
          }}
        />
      )}

      {cambiandoEstado && (
        <CambioEstadoUsuario
          usuario={cambiandoEstado}
          onCerrar={() => setCambiandoEstado(null)}
          onHecho={() => { setCambiandoEstado(null); cargar(); }}
        />
      )}

      {credencial && (
        <Dialogo
          titulo="Contraseña temporal"
          onCerrar={() => setCredencial(null)}
          pie={<button className="primario" onClick={() => setCredencial(null)}>Ya la anoté</button>}
        >
          <div className="aviso">
            Esta contraseña se muestra una sola vez y no se puede volver a
            consultar: en la base sólo queda su huella. Entrégasela a la
            persona por un medio seguro; al entrar tendrá que cambiarla.
          </div>
          <div className="campo">
            <label>Correo</label>
            <span className="mono">{credencial.correo}</span>
          </div>
          <div className="campo">
            <label>Contraseña temporal</label>
            <span className="mono" style={{ fontSize: 16 }}>{credencial.password}</span>
          </div>
        </Dialogo>
      )}
    </>
  );
}

function fecha(iso: string): string {
  return new Date(iso).toLocaleString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// ══════════════════════════════════════════════════════════ formulario

function FormularioUsuario({
  roles, regiones, usuario, onCerrar, onGuardado,
}: {
  roles: Rol[];
  regiones: Opcion[];
  usuario: UsuarioListado | null;
  onCerrar: () => void;
  onGuardado: (credencial?: { correo: string; password: string }) => void;
}) {
  const esAlta = usuario === null;
  const [datos, setDatos] = useState({
    nombre: usuario?.nombre ?? '',
    apellidos: usuario?.apellidos ?? '',
    correo: usuario?.correo ?? '',
    telefono: '',
  });
  const [rolesElegidos, setRolesElegidos] = useState<string[]>(
    usuario?.roles.map((r) => r.clave) ?? [],
  );
  const [zonasElegidas, setZonasElegidas] = useState<string[]>(
    usuario?.regiones.map((r) => r.id) ?? [],
  );
  const [zonasQueCubre, setZonasQueCubre] = useState<string[]>(
    usuario?.zonasQueCubre.map((r) => r.id) ?? [],
  );
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  function alternar(lista: string[], valor: string): string[] {
    return lista.includes(valor) ? lista.filter((v) => v !== valor) : [...lista, valor];
  }

  async function guardar() {
    setError(null);
    setTrabajando(true);
    try {
      if (esAlta) {
        const r = await api.post<{ id: string; passwordTemporal: string }>('/usuarios', {
          nombre: datos.nombre.trim(),
          apellidos: datos.apellidos.trim() || undefined,
          correo: datos.correo.trim().toLowerCase(),
          telefono: datos.telefono.trim() || undefined,
          roles: rolesElegidos,
          regiones: zonasElegidas,
          zonasQueCubre,
        });
        onGuardado({ correo: datos.correo.trim().toLowerCase(), password: r.passwordTemporal });
      } else {
        await api.patch(`/usuarios/${usuario!.id}`, {
          nombre: datos.nombre.trim(),
          apellidos: datos.apellidos.trim() || null,
          correo: datos.correo.trim().toLowerCase(),
          roles: rolesElegidos,
          regiones: zonasElegidas,
          zonasQueCubre,
        });
        onGuardado();
      }
    } catch (e) {
      setError(e);
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo={esAlta ? 'Nuevo usuario' : `Editar ${usuario!.correo}`}
      onCerrar={onCerrar}
      pie={
        <>
          <button onClick={onCerrar} disabled={trabajando}>Cancelar</button>
          <button
            className="primario"
            onClick={guardar}
            disabled={trabajando || rolesElegidos.length === 0}
          >
            {trabajando ? 'Guardando…' : 'Guardar'}
          </button>
        </>
      }
    >
      <Error error={error} />

      <div className="campo">
        <label htmlFor="nombre">Nombre *</label>
        <input
          id="nombre" value={datos.nombre} maxLength={120}
          onChange={(e) => setDatos({ ...datos, nombre: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="apellidos">Apellidos</label>
        <input
          id="apellidos" value={datos.apellidos} maxLength={150}
          onChange={(e) => setDatos({ ...datos, apellidos: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="correo">Correo *</label>
        <input
          id="correo" type="email" value={datos.correo} maxLength={150}
          onChange={(e) => setDatos({ ...datos, correo: e.target.value })}
        />
        <span className="ayuda">Es el identificador de acceso.</span>
      </div>

      {esAlta && (
        <div className="campo">
          <label htmlFor="telefono">Teléfono</label>
          <input
            id="telefono" value={datos.telefono} maxLength={30}
            onChange={(e) => setDatos({ ...datos, telefono: e.target.value })}
          />
        </div>
      )}

      <div className="campo">
        <label>Roles *</label>
        {roles.map((r) => (
          <label key={r.clave} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={rolesElegidos.includes(r.clave)}
              onChange={() => setRolesElegidos(alternar(rolesElegidos, r.clave))}
            />
            {r.nombre}
          </label>
        ))}
        <span className="ayuda">
          Con varios roles gana el más amplio: los permisos se suman.
        </span>
      </div>

      <div className="campo">
        <label>Zonas</label>
        {regiones.map((r) => (
          <label key={r.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={zonasElegidas.includes(r.id)}
              onChange={() => {
                const siguientes = alternar(zonasElegidas, r.id);
                setZonasElegidas(siguientes);
                // Cubrir una zona exige tenerla: al quitarla, se cae su
                // cobertura con ella en vez de mandarse al guardar y
                // volver con un error.
                setZonasQueCubre((previas) => previas.filter((z) => siguientes.includes(z)));
              }}
            />
            {r.nombre}
          </label>
        ))}
        <span className="ayuda">
          Sin ninguna marcada, un rol limitado por zona no ve nada. Los roles
          de alcance global ven todo sin importar esto.
        </span>
      </div>

      {/* Ver una zona y responder por ella son dos cosas distintas, y
          hasta la 0073 se guardaban en el mismo dato. La 0025 le dio
          todas las zonas a todos los supervisores para que vieran algo,
          asi que el bloque de arriba dejo de decir nada sobre quien
          cubre que. Karin: «si hay zonas espezificas que cubren, y es lo
          que necesito que se vea como responsable, aunque lo cargue otra
          persona».

          Solo se ofrecen las que esta persona tiene marcadas arriba:
          responsable de una zona que no ve seria un responsable que no
          puede abrir sus propias ordenes. Al destildar una zona, su
          cobertura se cae con ella. */}
      <div className="campo">
        <label>Zonas de las que responde</label>
        {zonasElegidas.length === 0 ? (
          <span className="ayuda">Marca primero las zonas de esta persona.</span>
        ) : (
          regiones
            .filter((r) => zonasElegidas.includes(r.id))
            .map((r) => (
              <label key={r.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={zonasQueCubre.includes(r.id)}
                  onChange={() => setZonasQueCubre(alternar(zonasQueCubre, r.id))}
                />
                {r.nombre}
              </label>
            ))
        )}
        <span className="ayuda">
          Las órdenes nuevas de estas zonas nacen a su nombre, las cargue
          quien las cargue. Una zona tiene un solo responsable.
        </span>
      </div>
    </Dialogo>
  );
}

function Restablecer({
  usuario, onCerrar, onHecho,
}: {
  usuario: UsuarioListado;
  onCerrar: () => void;
  onHecho: (password: string) => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  async function confirmar() {
    setError(null);
    setTrabajando(true);
    try {
      const r = await api.post<{ passwordTemporal: string }>(
        `/usuarios/${usuario.id}/restablecer-password`, { motivo },
      );
      onHecho(r.passwordTemporal);
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  return (
    <DialogoMotivo
      titulo="Restablecer contraseña"
      textoBoton="Restablecer"
      motivo={motivo}
      setMotivo={setMotivo}
      trabajando={trabajando}
      error={error}
      onConfirmar={confirmar}
      onCerrar={onCerrar}
      descripcion={
        <p>
          Se generará una contraseña temporal para <strong>{usuario.correo}</strong>.
          La anterior deja de servir en ese momento.
        </p>
      }
    />
  );
}

function CambioEstadoUsuario({
  usuario, onCerrar, onHecho,
}: {
  usuario: UsuarioListado;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  async function confirmar() {
    setError(null);
    setTrabajando(true);
    try {
      const accion = usuario.activo ? 'desactivar' : 'reactivar';
      await api.post(`/usuarios/${usuario.id}/${accion}`, { motivo });
      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  return (
    <DialogoMotivo
      titulo={`${usuario.activo ? 'Desactivar' : 'Reactivar'} usuario`}
      textoBoton={usuario.activo ? 'Desactivar' : 'Reactivar'}
      motivo={motivo}
      setMotivo={setMotivo}
      trabajando={trabajando}
      error={error}
      onConfirmar={confirmar}
      onCerrar={onCerrar}
      descripcion={
        <p>
          <strong>{usuario.correo}</strong>
          {usuario.activo
            ? ' no podrá volver a entrar. Su historial se conserva.'
            : ' podrá volver a entrar con su contraseña actual.'}
        </p>
      }
    />
  );
}
